import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject, Subject, from, mergeMap, toArray } from 'rxjs';
import { catchError, map, distinctUntilChanged, debounceTime, tap, take, retryWhen, delay, concat, switchMap, filter } from 'rxjs/operators';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { CustomSlug } from '../../models/CustomSlug.model';
import { CustomTemplateDTO } from '../../dto/template/customTemplate.dto';
import { CustomSlugDTO } from '../../dto/slug/customSlug.dto';
import { ComponentRegistryService } from '../component-registry/component-registry.service';
import { Store } from '@ngrx/store';
import * as TemplateActions from '../../ngrx/template/template.actions';
import { forkJoin } from 'rxjs';
import { CustomComponent } from '../../models/CustomComponent.model';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { environment } from '../../environment/environment';
import { ConnectivityService } from '../connectivity/connectivity.service';

@Injectable({
  providedIn: 'root'
})

export class TemplateService {
  public loadedStaticTemplate: CustomTemplate | undefined;
  public loadedDynamicTemplate: CustomTemplate | undefined;
  private currentViewportSize = new BehaviorSubject<{ width: number; height: number }>({ width: window.innerWidth, height: window.innerHeight });
  private useCustomViewport = false;
  private resizeSubject = new Subject<void>();
  private _currentMode = new BehaviorSubject<1 | 2 | 3>(1);
  private lastModeCalculation = 0;
  private isInitializing = false;
  private modeRecalculatedSubject = new Subject<1 | 2 | 3>();

  // Portal-specific slugs
  private adminPortalSlugs = [
    'dashboard', 
    'ressourcer',
    'lokationer',
    'brugere',
    'events',
    'indstillinger',
    'tab/ressourcer/ressourcer_overblik',
    'tab/ressourcer/instruktoerer',
    'tab/ressourcer/forplejning',
    'tab/ressourcer/materialer'
  ];

  private userPortalSlugs = [
    'profile',
    'events',
    'indstillinger'
  ];

  constructor(private http: HttpClient, private componentRegistry: ComponentRegistryService, private store: Store, private router: Router, private authService: AuthService, private connectivityService: ConnectivityService) {
    // Set up viewport size change detection
    this.currentViewportSize.pipe(
      distinctUntilChanged((prev, curr) => prev.width === curr.width),
      map(({ width }) => {
        const breakpoints = { small: environment.mobileWidth, medium: environment.tabletWidth };
        let newMode: 1 | 2 | 3;
        if (width <= breakpoints.small) {
          newMode = 3; // Mobile
        } else if (width <= breakpoints.medium) {
          newMode = 2; // Tablet
        } else {
          newMode = 1; // Desktop
        }
        return newMode;
      }),
      distinctUntilChanged() // Only emit when mode actually changes
    ).subscribe(newMode => {
      // Don't override during initialization to prevent race conditions
      if (!this.isInitializing && this._currentMode.value !== newMode) {
        this._currentMode.next(newMode);
        this.store.dispatch(TemplateActions.setCurrentMode({ mode: newMode }));
      }
    });

    // Set up resize event handling
    this.resizeSubject.pipe(
      debounceTime(200)  // Debounce resize events
    ).subscribe(() => {
      if (!this.useCustomViewport) {
        this.currentViewportSize.next({
          width: Math.round(window.visualViewport?.width || window.innerWidth),
          height: Math.round(window.visualViewport?.height || window.innerHeight)
        });
      }
    });

    window.addEventListener('resize', () => this.resizeSubject.next());
    window.visualViewport?.addEventListener('resize', () => this.resizeSubject.next());

    // Initialize the mode based on current viewport size
    // Use multiple strategies to ensure it works on initial load
    this.initializeModeWithFallbacks();
  }

  private initializeModeWithFallbacks(): void {
    // Strategy 1: Immediate initialization
    this.initializeMode();
    
    // Strategy 2: After a short delay to ensure DOM is ready
    setTimeout(() => this.initializeMode(), 0);
    
    // Strategy 3: After DOM content is loaded (if not already loaded)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeMode());
    }
    
    // Strategy 4: After window is fully loaded (if not already loaded)
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.initializeMode());
    }
  }

  private initializeMode(): void {
    // Throttle mode calculations to prevent excessive calls
    const now = Date.now();
    if (now - this.lastModeCalculation < 100) {
      return;
    }
    this.lastModeCalculation = now;

    // Set initialization flag to prevent race conditions
    this.isInitializing = true;

    const currentWidth = Math.round(
      window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth
    );
    const currentHeight = Math.round(
      window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight
    );
    const breakpoints = { small: environment.mobileWidth, medium: environment.tabletWidth };
    
    
    let initialMode: 1 | 2 | 3;
    
    if (currentWidth <= breakpoints.small) {
      initialMode = 3; // Mobile
    } else if (currentWidth <= breakpoints.medium) {
      initialMode = 2; // Tablet
    } else {
      initialMode = 1; // Desktop
    }
    
    
    // Always set the mode during initialization
    this._currentMode.next(initialMode);
    this.store.dispatch(TemplateActions.setCurrentMode({ mode: initialMode }));
    
    // Update the viewport size and allow subscription to work normally after a delay
    this.currentViewportSize.next({ width: currentWidth, height: currentHeight });
    
    // Clear initialization flag after a short delay to allow normal operation
    setTimeout(() => {
      this.isInitializing = false;
    }, 500);
  }
  
  /*getViewportSize(mode: 1 | 2 | 3) {
    switch (mode) {
      case 1: // Desktop
        return { width: 1920, height: 1080 };
      case 2: // Tablet
        return { width: environment.tabletWidth, height: 768 };
      case 3: // Mobile
        return { width: environment.mobileWidth, height: 667 };
    }
  }

  setCustomViewportSize(width: number, height: number) {
    this.useCustomViewport = true;
    this.currentViewportSize.next({ width, height });
  }

  resetToWindowViewport() {
    this.useCustomViewport = false;
    this.currentViewportSize.next({ width: window.innerWidth, height: window.innerHeight });
  }*/

  get currentMode$(): Observable<1 | 2 | 3> {
    return this._currentMode.asObservable();
  }

  get modeRecalculated$(): Observable<1 | 2 | 3> {
    return this.modeRecalculatedSubject.asObservable();
  }

  setMode(mode: 1 | 2 | 3) {
    // Only update if the mode is actually different
    if (this._currentMode.value !== mode) {
      this._currentMode.next(mode);
      this.store.dispatch(TemplateActions.setCurrentMode({ mode }));
    }
  }

  /**
   * Force recalculation of the current mode based on viewport size
   * This can be called from components to ensure proper scaling
   */
  recalculateMode(): void {
    // Temporarily disable the initialization flag for manual recalculation
    const wasInitializing = this.isInitializing;
    this.isInitializing = false;
    const previousMode = this._currentMode.value;
    this.initializeMode();
    this.isInitializing = wasInitializing;
    
    // Emit the recalculated mode to notify components
    this.modeRecalculatedSubject.next(this._currentMode.value);
  }

  /**
   * Get the current mode synchronously
   */
  getCurrentMode(): 1 | 2 | 3 {
    return this._currentMode.value;
  }

  /***
   * Get page by slug
   */
  getPageDataBySlug(slug: string, isCurrentPageRequest: boolean = false): Observable<{ data: { customSlug: CustomSlug | null; customTemplateArr: CustomTemplate[] }; statusCode: number }> {
    // Normalize slug by removing leading slashes
    const normalizedSlug = slug.replace(/^\/+/, '');
    
    const url = `${environment.apiUrl}/slugs`;
    return this.authService.authStateReady$.pipe(
      take(1),
      mergeMap(() => this.authService.isAuthenticated$),
      take(1),
      mergeMap(isAuthenticated => {
        return this.http.post<{ customSlugDTO: CustomSlugDTO; customTemplateDTOArr: CustomTemplateDTO[] }>(
          url,
          { slug: normalizedSlug },
          { 
            observe: 'response', 
            withCredentials: true,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        ).pipe(
          tap(response => {
            const contentLength = response.headers.get('content-length');
            const contentType = response.headers.get('content-type');
            }),
          map(response => {
            // Check if we have a valid JSON response
            if (response.body && Object.keys(response.body).length > 0) {
              if (!response.body.customSlugDTO) {
                console.error('Response body missing customSlugDTO:', response.body);
                throw new Error('INVALID_RESPONSE');
              }

              return {
                data: {
                  customSlug: CustomSlug.fromDTO(response.body.customSlugDTO),
                  customTemplateArr: response.body.customTemplateDTOArr?.map(template => CustomTemplate.fromDTO(template)) || [],
                },
                statusCode: response.status
              };
            }
            
            // If we reach here, we have an empty or invalid response
            console.warn('Empty or invalid response received for slug:', normalizedSlug);
            return {
              data: {
                customSlug: null,
                customTemplateArr: [],
              },
              statusCode: response.status
            };
          }),
          retryWhen(errors => 
            errors.pipe(
              mergeMap(error => {
                // Don't retry for 404s or specific error types
                if (error instanceof HttpErrorResponse && error.status === 404) {
                  return throwError(() => error);
                }
                if (error.message === 'INVALID_RESPONSE') {
                  return throwError(() => error);
                }
                
                // Check if the error is due to network connectivity
                const isNetworkError = !this.connectivityService.isConnected() || 
                                     (error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500));
                
                if (isNetworkError) {
                  // Wait for connectivity before retrying
                  return this.connectivityService.waitForConnection().pipe(
                    filter(isConnected => isConnected),
                    take(1),
                    delay(1000), // Brief delay after connectivity is restored
                    switchMap(() => of(error))
                  );
                }
                
                return of(error);
              }),
              delay(1000),
              take(3),
              concat(throwError(() => new Error('Failed to load after retries')))
            )
          ),
          catchError((error: HttpErrorResponse | Error) => {
            console.error('Error fetching custom slug:', {
              error,
              type: error.constructor.name,
              status: error instanceof HttpErrorResponse ? error.status : 'N/A',
              message: error.message
            });

            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                if (isAuthenticated) {
                  this.router.navigate(['/401']);
                } else {
                  this.router.navigate(['/login']);
                }
              }
              
              switch (error.status) {
                case 404:
                  {
                    if (isCurrentPageRequest) {
                      
                      // Force a page redirect using window.location
                      // This is the most direct approach and avoids Angular router issues
                      window.location.href = '/404';
                    }
                    
                    // Return a structured error object with the slug for better error tracking
                    const errorObj = {
                      message: 'Page not found',
                      status: 404,
                      slug: normalizedSlug
                    };
                    return throwError(() => errorObj);
                  }
                case 403:
                  return throwError(() => new Error('Access denied'));
                case 500:
                  return throwError(() => new Error('Server error, please try again later'));
                default:
                  return throwError(() => new Error(`Failed to load page data: ${error.message}`));
              }
            }

            return throwError(() => error);
          })
        );
      })
    );
  }

  /***
   * Get custom templates
   */
  getCustomTemplates(): Observable<CustomTemplate[]> {
    return this.http.get<CustomTemplateDTO[]>(`${environment.apiUrl}/templates`).pipe(
      catchError(error => {
        console.error('Error fetching custom templates:', error);
        return of([]); // Return an empty array in case of error
      }),
      map(result => {
        if (result) {
          var templates = result.map(template => CustomTemplate.fromDTO(template));
          return templates;
        } else {
          throw new Error('No custom templates found');
        }
      })
    );
  }

  /***
   * Get custom template
   */
  getCustomTemplate(slug: CustomSlug): Observable<CustomTemplate> {
    return this.http.get<CustomTemplateDTO>(`${environment.apiUrl}/test/customtemplates/${slug.slug}`).pipe(
      catchError(error => {
        console.error('Error fetching custom template:', error);
        return throwError(() => new Error('Failed to fetch custom template: ' + slug.slug));
      }),
      map(result => {
        if (result) {
          return CustomTemplate.fromDTO(result);
        } else {
          throw new Error('No custom template found');
        }
      })
    );
  }

  
  /*
  * Post custom template
  */
  postTemplate(customTemplate: CustomTemplate): Observable<string> {
    const customTemplateDTO = CustomTemplate.convertToDTO(customTemplate);
    return this.http.post<string>(`${environment.apiUrl}/templates`, { template: customTemplateDTO }).pipe(
      catchError(error => {
        console.error('Error posting custom template:', error);
        return throwError(() => error); // or return EMPTY if you want to complete the stream
      })
    );
  }

  /*
  * Get slugs
  */
  getSlugs(): Observable<CustomSlug[]> {
    return this.http.get<CustomSlugDTO[]>(`${environment.apiUrl}/slugs`).pipe(
      catchError(error => {
        console.error('[TemplateService] Error fetching slugs:', error);
        return of([]);
      }),
      map(result => {
        if (result) {
          const slugs = result
            .map(slug => CustomSlug.fromDTO(slug))
            .sort((a, b) => a.slug.localeCompare(b.slug));
          return slugs;
        } else {
          console.warn("[TemplateService] No slugs found in response");
          throw new Error('No slugs found');
        }
      })
    );
  }

  /*
  * Get slug
  */
  getSlug(slug: string): Observable<CustomSlug> {
    return this.http.get<CustomSlugDTO>(`${environment.apiUrl}/slugs/${slug}`, {
      withCredentials: true
    }).pipe(
      map((response) => CustomSlug.fromDTO(response)),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching slug:', error);
        if (error.status === 401) {
          this.router.navigate(['/401']);
        }
        return throwError(() => error);
      })
    );
  }

  /*
  * Post slug
  */
  postSlug(customSlug: CustomSlug): Observable<string> {
    const customSlugDTO = CustomSlug.convertToDTO(customSlug);
    return this.http.put<string>(`${environment.apiUrl}/slugs`, {slug: customSlugDTO});
  }

  /*
  * Preload slugs
  */
  preloadSlugs(slugs: string[]): Observable<any[]> {
    // Normalize slugs by removing leading slashes
    const normalizedSlugs = slugs.map(slug => slug.replace(/^\/+/, ''));
    
    return this.store.select((state: any) => state.template?.slugs ?? {}).pipe(
      take(1),
      switchMap((slugsState) => {
        const slugsToFetch = normalizedSlugs.filter(
          (slug) => !slugsState[slug]?.loaded
        );

        if (slugsToFetch.length === 0) {
          return of([]);
        }

        return from(slugsToFetch).pipe(
          mergeMap((slug) =>
            this.getPageDataBySlug(slug, false).pipe(
              tap((response) => {
                if (!response || !response.data) {
                  return;
                }

                const { customSlug, customTemplateArr } = response.data;
                let staticTemplate, dynamicTemplate;

                if (Array.isArray(customTemplateArr)) {
                  staticTemplate = customTemplateArr.find(
                    (t) => Number(t.isstatic) === 1
                  );
                  dynamicTemplate = customTemplateArr.find(
                    (t) => Number(t.isstatic) === 0
                  );
                }

                if (staticTemplate) {
                  this.store.dispatch(
                    TemplateActions.loadStaticTemplateSuccess({
                      slug,
                      staticTemplate,
                    })
                  );
                }

                if (dynamicTemplate) {
                  this.store.dispatch(
                    TemplateActions.loadDynamicTemplateSuccess({
                      slug,
                      dynamicTemplate,
                    })
                  );
                }

                if (customSlug) {
                  this.store.dispatch(
                    TemplateActions.loadCustomSlugSuccess({
                      slug,
                      customSlug,
                    })
                  );
                }

                this.store.dispatch(
                  TemplateActions.loadPageDataSuccess({
                    slug,
                    templatesChanged: true,
                    hasCustomSlug: !!customSlug,
                    hasTemplates: !!(staticTemplate || dynamicTemplate),
                    data: response.data,
                  })
                );
              }),
              catchError((error) => {
                console.error(
                  `[TemplateService] Error preloading slug ${slug}:`,
                  error
                );
                this.store.dispatch(
                  TemplateActions.loadPageDataFailure({ error })
                );
                return of(null);
              })
            )
          ),
          toArray()
        );
      })
    );
  }

  preloadPortalSlugs(portal: string): Observable<any[]> {
    const slugsToPreload = portal === 'administration' ? this.adminPortalSlugs : this.userPortalSlugs;
    return this.preloadSlugs(slugsToPreload);
  }

  updateContainerChildren(containerUUID: string, childComponents: CustomComponent[], mode: 1 | 2 | 3) {
    // Find the current template and update its container's children
    const currentTemplate = this.loadedDynamicTemplate;
    if (currentTemplate) {
      const containerComponent = currentTemplate.customComponents.find(comp => comp.uuid === containerUUID);
      if (containerComponent) {
        containerComponent.childComponents = childComponents;
      }
    }
  }

  /*
  * Save custom slug
  */
  saveCustomSlug(customSlugDTO: CustomSlugDTO): Observable<CustomSlug> {
    return this.http.put<CustomSlugDTO>(`${environment.apiUrl}/slugs`, { slug: customSlugDTO }).pipe(
      map(response => CustomSlug.fromDTO(response)),
      catchError(error => {
        console.error('Error saving custom slug:', error);
        return throwError(() => error);
      })
    );
  }
}
